import { LockIcon, LockOpenIcon } from "lucide-react";
import { useMemo } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PublicEvent,
  PublicRoomSystem,
  RoomSystemVisibility,
} from "@classview/shared-types";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { RoomSystemActions } from "./room-system-actions";

type RoomSystemsTableProps = {
  roomSystems: PublicRoomSystem[];
  isOrganisationFieldShown?: boolean;
  event: PublicEvent;
};

export function RoomSystemsTable({
  roomSystems,
  isOrganisationFieldShown = true,
  event,
}: RoomSystemsTableProps) {
  const columnHelper = createColumnHelper<PublicRoomSystem>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "name",
        header: "Room Systems",
        size: 999,
        cell: ({ row }) => {
          return (
            <div>
              <span>{row.original.name}</span>
              <div className="flex items-center gap-1">
                {row.original.visibility === RoomSystemVisibility.PUBLIC ? (
                  <>
                    <LockOpenIcon
                      className="size-3 text-blue-600"
                      aria-hidden={true}
                    />
                    <span className="text-muted-foreground">Public</span>
                  </>
                ) : (
                  <>
                    <LockIcon
                      className="size-3 text-green-600"
                      aria-hidden={true}
                    />
                    <span className="text-muted-foreground">Private</span>
                  </>
                )}
              </div>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "type",
        header: "Type",
        size: 100,
        cell: ({ row }) => {
          return (
            <div className="capitalize text-muted-foreground">
              {row.original.type.toLowerCase()}
            </div>
          );
        },
      }),
      ...(isOrganisationFieldShown
        ? [
            columnHelper.display({
              id: "organisation",
              header: "Organisation",
              size: 150,
              cell: ({ row }) => {
                return (
                  <div className="text-muted-foreground">
                    {row.original.organisation.name}
                  </div>
                );
              },
            }),
          ]
        : []),
      columnHelper.display({
        id: "actions",
        size: 70,
        cell: ({ row }) => {
          return (
            <RoomSystemActions event={event} roomSystemId={row.original.id} />
          );
        },
      }),
    ],
    [columnHelper, isOrganisationFieldShown, event],
  );
  const table = useReactTable({
    data: roomSystems ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (roomSystems.length === 0) {
    return;
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                style={{
                  width: header.getSize() === 999 ? "auto" : header.getSize(),
                }}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
